import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import { Activity } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { API } from "@/data/content";

const SYMBOLS = {
  TSLA: { name: "Tesla, Inc.", base: 248 },
  NVDA: { name: "Nvidia Corp.", base: 132 },
  MSFT: { name: "Microsoft Corp.", base: 428 },
};
const TIMEFRAMES = { "1D": [48, 0.005], "1W": [56, 0.011], "1M": [30, 0.02], "1Y": [52, 0.04], ALL: [100, 0.075] };

const pseudo = (seed, i) => {
  const x = Math.sin(seed * 127.1 + i * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

function genSeries(symbol, tf) {
  const [points, vol] = TIMEFRAMES[tf];
  const seed = symbol.charCodeAt(0) + symbol.charCodeAt(1) + tf.charCodeAt(0);
  const target = SYMBOLS[symbol].base;
  let v = target * (1 - vol * points * 0.28);
  return Array.from({ length: points }, (_, i) => {
    v = Math.max(v * (1 + (pseudo(seed, i) - 0.42) * vol * 2), target * 0.35);
    return { i, v: +v.toFixed(2) };
  });
}

const SECTORS = [
  { name: "AI Semiconductors", score: 92 },
  { name: "Cloud Infrastructure", score: 78 },
  { name: "Consumer Technology", score: 71 },
  { name: "EV & Energy", score: 64 },
  { name: "E-Commerce & Logistics", score: 58 },
  { name: "Quantum Research", score: 41 },
];

const INDICATORS = [
  { label: "US 10Y Yield", value: "4.21%", delta: "+0.03" },
  { label: "VIX Volatility", value: "13.82", delta: "-0.64" },
  { label: "DXY Dollar Index", value: "104.26", delta: "+0.18" },
  { label: "NASDAQ-100", value: "19,755", delta: "+1.1%" },
];

export default function MarketDashboard() {
  const [symbol, setSymbol] = useState("TSLA");
  const [tf, setTf] = useState("1M");
  const [liveQuotes, setLiveQuotes] = useState(null);
  const data = useMemo(() => genSeries(symbol, tf), [symbol, tf]);
  const lq = liveQuotes?.[symbol];
  const last = lq ? lq.price : data[data.length - 1].v;
  const first = data[0].v;
  const change = lq
    ? lq.change_pct.toFixed(2)
    : (((last - first) / first) * 100).toFixed(2);
  const high = Math.max(...data.map((d) => d.v));
  const low = Math.min(...data.map((d) => d.v));

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/quotes`);
        if (alive && data.live)
          setLiveQuotes(
            Object.fromEntries(data.quotes.map((q) => [q.symbol, q]))
          );
      } catch {
        /* simulated mode */
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  return (
    <section
      id="terminal"
      data-testid="market-dashboard"
      className="relative border-y border-line bg-surface py-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <Eyebrow>Market Dashboard</Eyebrow>
              <SectionTitle className="mt-3">
                The X <span className="text-brand">Terminal</span>
              </SectionTitle>
            </div>
            <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
              <Activity size={12} className="text-brand" />
              Simulated data — educational purposes only
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="overflow-hidden rounded-2xl border border-line bg-panel">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-line px-5 py-4">
              <div className="flex items-center gap-4">
                <select
                  data-testid="dashboard-symbol-select"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value)}
                  className="rounded-lg border border-line bg-surface px-3 py-2 font-mono text-xs font-bold text-ink outline-none focus:border-brand"
                >
                  {Object.entries(SYMBOLS).map(([sym, s]) => (
                    <option key={sym} value={sym}>
                      {sym} — {s.name}
                    </option>
                  ))}
                </select>
                <div>
                  <p className="flex items-center gap-2 font-display text-2xl font-black leading-none text-ink">
                    ${last.toLocaleString()}
                    {lq && (
                      <span
                        data-testid="dashboard-live-badge"
                        className="rounded-full bg-up/15 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-up"
                      >
                        Live
                      </span>
                    )}
                  </p>
                  <p
                    className={`font-mono text-[11px] font-bold ${
                      +change >= 0 ? "text-up" : "text-brand"
                    }`}
                  >
                    {+change >= 0 ? "+" : ""}
                    {change}% {lq ? "today" : "period"}
                  </p>
                </div>
              </div>
              <div className="flex gap-1.5">
                {Object.keys(TIMEFRAMES).map((k) => (
                  <button
                    key={k}
                    data-testid={`dashboard-timeframe-${k.toLowerCase()}`}
                    onClick={() => setTf(k)}
                    className={`relative rounded-md px-3.5 py-1.5 font-mono text-[11px] font-bold transition-colors duration-200 ${
                      tf === k ? "text-white" : "text-dim hover:text-ink"
                    }`}
                  >
                    {tf === k && (
                      <motion.span
                        layoutId="tf-pill"
                        className="absolute inset-0 rounded-md bg-brand"
                        transition={{ type: "spring", stiffness: 400, damping: 32 }}
                      />
                    )}
                    <span className="relative">{k}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="h-72 px-2 pt-4 sm:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="xg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF2A38" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#FF2A38" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="i" hide />
                  <YAxis
                    domain={["auto", "auto"]}
                    orientation="right"
                    tick={{ fill: "var(--text-3)", fontSize: 10, fontFamily: "JetBrains Mono" }}
                    axisLine={false}
                    tickLine={false}
                    width={56}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--line)",
                      borderRadius: 8,
                      fontFamily: "JetBrains Mono",
                      fontSize: 11,
                    }}
                    labelStyle={{ display: "none" }}
                    formatter={(v) => [`$${v}`, symbol]}
                  />
                  <Area
                    type="monotone"
                    dataKey="v"
                    stroke="#FF2A38"
                    strokeWidth={2}
                    fill="url(#xg)"
                    animationDuration={700}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-2 gap-px border-t border-line bg-line sm:grid-cols-4">
              {[
                ["Period High", `$${high.toLocaleString()}`],
                ["Period Low", `$${low.toLocaleString()}`],
                ["Avg Volume", "42.6M"],
                ["Volatility", tf === "1D" ? "Low" : tf === "ALL" ? "High" : "Medium"],
              ].map(([k, v]) => (
                <div key={k} className="bg-panel px-5 py-3.5">
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-dim">{k}</p>
                  <p className="mt-0.5 font-mono text-sm font-bold text-ink">{v}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Reveal delay={0.15}>
            <div className="h-full rounded-2xl border border-line bg-panel p-6">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-brand">
                Sector Performance
              </p>
              <div className="mt-5 space-y-4">
                {SECTORS.map((s, i) => (
                  <div key={s.name}>
                    <div className="mb-1.5 flex justify-between font-mono text-[11px]">
                      <span className="text-mute">{s.name}</span>
                      <span className="font-bold text-ink">{s.score}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-line">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${s.score}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.1, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                        className={`h-full rounded-full ${
                          i === 0 ? "bg-brand" : "bg-gradient-to-r from-brand-garnet to-brand"
                        }`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.22}>
            <div className="grid h-full grid-cols-2 gap-4">
              {INDICATORS.map((ind) => (
                <div
                  key={ind.label}
                  className="card-trace flex flex-col justify-between rounded-2xl border border-line bg-panel p-5"
                >
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-dim">
                    {ind.label}
                  </p>
                  <div className="mt-4">
                    <p className="font-display text-3xl font-black text-ink">{ind.value}</p>
                    <p
                      className={`font-mono text-[11px] font-bold ${
                        ind.delta.startsWith("-") ? "text-brand" : "text-up"
                      }`}
                    >
                      {ind.delta}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.15em] text-dim">
          Chart series are simulated illustrations. Headline prices switch to
          live Finnhub quotes automatically once a market data key is connected.
        </p>
      </div>
    </section>
  );
}
